<script setup lang="ts">
import { GoogleMap, InfoWindow } from 'vue3-google-map'

import { findOfDeliveryArea } from '@/utils/findOfDeliveryArea'

import type { ServiceSettings } from '@/store/city/city.types'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'

import {
  EmitDataOfMap,
  GoogleClickEvent,
  IGoogleCoords,
  TypesEntity,
} from '@/types/mapServices'

import GoogleMapClickedMarker from '@/components/Services/GoogleMap/GoogleMapClickedMarker.vue'
import GoogleMapPoints from '@/components/Services/GoogleMap/GoogleMapPoints.vue'
import GoogleMapPolygons from '@/components/Services/GoogleMap/GoogleMapPolygons.vue'

import type {
  AreaProperties,
  LatLng,
  PointProperties,
  PolygonProperties,
} from '@/api/modules/delivery/types'
import { Loader } from '@googlemaps/js-api-loader'

const LANGUAGE_GOOGLE_MAP = 'ru'

type Props = {
  settings: ServiceSettings
  areas: AreaProperties[]
  points: PointProperties[]
  clickedRestaurant: PointProperties | null
  selectedAddress: DeliveryDataAddress
}

const props = withDefaults(defineProps<Props>(), {
  areas: () => [],
  points: () => [],
})

const emit = defineEmits([
  'onClickMap',
  'onClickRestaurant',
  'onUpdateCenterMap',
  'onUpdateDistance',
])

// Координаты маркера на карте
const clickedMarkerCoordinates = ref<IGoogleCoords | null>(null)

// Информация доставке адреса
const deliveryAddressDetails = ref<PolygonProperties | null>(null)

const refGoogleMap = ref<InstanceType<typeof GoogleMap>>()

const balloonOfRestaurant = ref<InstanceType<typeof InfoWindow>>()

const loaderGoogleInstance = new Loader({
  apiKey: props.settings.api_key,
  language: LANGUAGE_GOOGLE_MAP,
})

const promiseLoadMap = loaderGoogleInstance.load()

const positionCenterMap: google.maps.LatLngLiteral = {
  lat: props.settings.coords[0],
  lng: props.settings.coords[1],
}

const optionsInfoWindow = computed(() => {
  if (!props.clickedRestaurant) return

  const [lat, lng] = props.clickedRestaurant.coordinates

  return {
    position: { lat, lng },
    content: props.clickedRestaurant.address,
  }
})

// Сетим данные, если выбран ресторан из списка
watch(
  () => props.clickedRestaurant,
  (newPoint: PointProperties | null) => {
    if (!newPoint) return

    const { lat, lon } = newPoint.coordinates

    const coords = { lat, lng: lon }

    setLocationMap(coords)

    emit('onUpdateCenterMap')

    balloonOfRestaurant?.value?.open()
  },
)

// Обновляем карту если обновился autocomplete
watch(
  () => props.selectedAddress?.coordinates,
  (newCoordsOfAddress: LatLng) => {
    const coords = {
      lat: newCoordsOfAddress[0],
      lng: newCoordsOfAddress[1],
    }

    setLocationMap(coords)

    clickedMarkerCoordinates.value = coords

    emit('onUpdateDistance', [coords.lat, coords.lng])

    // TO-DO: В будущем вынести рассчет в WebWorker
    updateBalloonOfMarker(newCoordsOfAddress)
  },
)

const setLocationMap = (coords: IGoogleCoords, zoom: number = 16) => {
  refGoogleMap?.value?.map?.panTo(coords)
  refGoogleMap?.value?.map?.setZoom(zoom)
}

const updateBalloonOfMarker = (coords: LatLng) => {
  const polygon = findOfDeliveryArea(props.areas, coords)

  deliveryAddressDetails.value = polygon ? polygon.properties : null
}

// Handle клика вне зоны доставки
const clickedToMap = (event: GoogleClickEvent) => {
  deliveryAddressDetails.value = null

  clickedMarkerCoordinates.value = {
    lat: event.latLng.lat(),
    lng: event.latLng.lng(),
  }

  emit('onClickMap', [event.latLng.lng(), event.latLng.lat()])
}

// Handle клика маркера или точки в зоне доставки
const clickedToMapOfElement = (event: EmitDataOfMap) => {
  setLocationMap(event.coords)

  switch (event.type) {
    case TypesEntity.MARKER:
      if (!event.point) break

      balloonOfRestaurant.value?.open()

      emit('onClickRestaurant', event.point)

      break
    case TypesEntity.FEATURE:
      emit('onClickMap', [event.coords.lng, event.coords.lat])

      clickedMarkerCoordinates.value = event.coords

      updateBalloonOfMarker([event.coords.lng, event.coords.lat])

      break

    default:
      break
  }
}
</script>

<template>
  <google-map
    ref="refGoogleMap"
    :api-promise="promiseLoadMap"
    :center="positionCenterMap"
    :zoom="settings.zoom"
    :fullscreen-control="false"
    :map-type-control="false"
    :pan-control="false"
    :scale-control="false"
    @click="clickedToMap"
  >
    <google-map-points
      v-if="points.length"
      :points="points"
      :clicked-restaurant="clickedRestaurant"
      @clicked-to-map-of-element="clickedToMapOfElement"
    />

    <google-map-polygons
      v-if="areas"
      :areas="areas"
      @clicked-to-map-of-element="clickedToMapOfElement"
    />

    <info-window
      v-if="clickedRestaurant"
      ref="balloonOfRestaurant"
      :options="optionsInfoWindow"
    />

    <google-map-clicked-marker
      v-if="clickedMarkerCoordinates"
      :delivery-address-details="deliveryAddressDetails"
      :clicked-marker-coordinates="clickedMarkerCoordinates"
      :selected-address="selectedAddress"
    />
  </google-map>
</template>
